// Money-out categories for the Monthly Bank Summary.
//
// Every category has a "kind" that decides how it is treated in profit:
//   business            → business expense, reduces profit
//   owner_reimbursable  → owner paid a business cost, reimbursed; reduces profit
//   owner_draw          → personal money taken by the owner; does NOT reduce
//                         profit (shows as an owner draw, broken out by type)
//   transfer            → moving money between your own accounts; ignored
//   repayment           → paying back reimbursable money in; ignored for profit,
//                         reduces the "still owed back" balance
//
// Built-in categories live here. Categories you add on the page are saved to
// Firestore at appSettings/bankSpendCategories and merged in.

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";

export const KINDS = {
  business: { label: "Business expense", short: "Business", counts: true },
  owner_reimbursable: { label: "Owner reimbursable (business)", short: "Owner reimb.", counts: true },
  owner_draw: { label: "Owner draw (personal)", short: "Owner draw", counts: false },
  transfer: { label: "Transfer between accounts", short: "Transfer", counts: false },
  repayment: { label: "Paying back reimbursable money", short: "Pay-back", counts: false },
};
export const KIND_ORDER = ["business", "owner_reimbursable", "owner_draw", "repayment", "transfer"];

// Money out that pays back reimbursable money in (owner's personal money or a
// loan). Not a business expense and not a personal draw.
export const REPAYMENT_TYPE = "Paying Back Reimbursable";

const B = (name) => ({ name, kind: "business" });
const O = (name) => ({ name, kind: "owner_draw" });

export const DEFAULT_CATEGORIES = [
  // Business
  B("Materials & Supplies"),
  B("Tools & Equipment"),
  B("Equipment Rental"),
  B("Subcontractors"),
  B("Payroll / Wages"),
  B("Payroll Taxes"),
  B("Contract Labor"),
  B("Gas / Fuel"),
  B("Vehicle Repairs & Maintenance"),
  B("Vehicle Payment / Lease"),
  B("Tolls & Parking"),
  B("Phones"),
  B("Internet"),
  B("Insurance - General Liability"),
  B("Insurance - Auto"),
  B("Insurance - Workers Comp"),
  B("Insurance - Health (Business)"),
  B("Insurance - Other"),
  B("Business Entertainment"),
  B("Business Meals"),
  B("Travel (Business)"),
  B("Rent / Office / Shop"),
  B("Utilities"),
  B("Software & Subscriptions"),
  B("Advertising & Marketing"),
  B("Website & Hosting"),
  B("Office Supplies"),
  B("Postage & Shipping"),
  B("Licenses & Permits"),
  B("Professional Fees (Legal / Accounting)"),
  B("Bank Fees"),
  B("Merchant / Card Processing Fees"),
  B("Loan Payment"),
  B("Interest"),
  B("Credit Card Payment"),
  B("Taxes (Business)"),
  B("Training & Education"),
  B("Uniforms & Safety Gear"),
  B("Dump / Disposal Fees"),
  B("Storage"),
  B("Cleaning & Janitorial"),
  B("Repairs & Maintenance (Shop)"),
  B("Donations / Sponsorships"),
  B("Gifts (Clients)"),
  B("Other Business"),
  // Owner reimbursable
  { name: "Owner Reimbursable", kind: "owner_reimbursable" },
  { name: "Owner Reimbursable - Mileage", kind: "owner_reimbursable" },
  // Owner draw (personal)
  O("Owner Draw (General)"),
  O("Personal"),
  O("Personal Entertainment"),
  O("Personal Dining"),
  O("Personal Shopping"),
  O("Personal Groceries"),
  O("Personal Travel"),
  O("Personal Auto / Gas"),
  O("Personal Phone"),
  O("Personal Bills / Utilities"),
  O("Personal Medical"),
  O("Personal Insurance"),
  O("Personal Loan / Credit Card"),
  O("Personal Cash Withdrawal"),
  O("Personal Gifts"),
  O("Personal Subscriptions"),
  O("Personal Home / Rent / Mortgage"),
  O("Personal Education / Childcare"),
  // Paying back reimbursable money in
  { name: "Paying Back Reimbursable", kind: "repayment" },
  // Transfers
  { name: "Transfer Between Accounts", kind: "transfer" },
  { name: "Transfer to Savings", kind: "transfer" },
];

const SETTINGS_REF = () => doc(db, "appSettings", "bankSpendCategories");

/** Built-in + custom, custom wins on a name clash, sorted within each kind. */
export const mergeCategories = (custom = []) => {
  const byName = new Map();
  DEFAULT_CATEGORIES.forEach((c) => byName.set(c.name.toLowerCase(), { ...c, builtIn: true }));
  custom.forEach((c) => {
    if (!c?.name || !KINDS[c.kind]) return;
    byName.set(c.name.trim().toLowerCase(), { name: c.name.trim(), kind: c.kind, builtIn: false });
  });
  return [...byName.values()].sort(
    (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || a.name.localeCompare(b.name)
  );
};

export const loadCustomCategories = async () => {
  try {
    const snap = await getDoc(SETTINGS_REF());
    return snap.exists() ? snap.data().custom || [] : [];
  } catch (e) {
    console.warn("Could not load custom categories", e);
    return [];
  }
};

export const saveCustomCategories = async (custom) => {
  await setDoc(SETTINGS_REF(), { custom, updatedAt: serverTimestamp() }, { merge: true });
};

/** Which kind a saved line belongs to. The kind stored on the line wins, so a
 * later rename of the category list never changes an old month's numbers. */
export const lineKind = (line, categories) => {
  if (line?.kind && KINDS[line.kind]) return line.kind;
  const found = categories.find((c) => c.name.toLowerCase() === String(line?.category || "").toLowerCase());
  return found ? found.kind : "business";
};
